import { FormButtonBox } from "@/features/standard/Form/FormButtonBox";
import { FormMain } from "@/features/standard/Form/FormMain";
import { FormWrapper } from "@/features/standard/Form/FormWrapper";

import { mutateInventories } from "@/api/inventories/useInventoriesQuery";
import { useModal } from "@/features/modal/ModalStackManager";
import { MultiBarcodeInput } from "@/features/multiBarcodeInput/MultiBarcodeInput";
import customAlert from "@/features/ui/alert/alert";
import { InventoriesAutoComplete } from "@/features/ui/autoComplete/inventoryLot/inventory-autoComplete";
import { LocationAutoComplete } from "@/features/ui/autoComplete/location/location-autoComplate";
import { MantineSelectableTable } from "@/features/ui/table/MantineSelectableTable";
import { useWorkActions } from "@/features/workByManagement/hooks/useWorkActions";
import { useInventoriesFormFetch } from "@/hooks/useInventoriesFormFetch";
import { handleErrorResponse } from "@/utils/errorMessage";
import { Button, Flex, NumberInput, Select, TextInput } from "@mantine/core";
import { useForm } from "@mantine/form";
import {
  AuthSignupPost201ResponseEquipmentWorksInner,
  ItemsGet200ResponseRowsInnerLotsInnerInventoriesInner,
  ProductionActionApiWorksBulkInputPutRequest,
  ProductionPlansGet200ResponseRowsInnerWorksInner,
  WorksBulkInputPutRequestWorkLotPairsInner,
} from "@sizlcorp/sizl-api-document/dist/models";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { MRT_ColumnDef, MRT_Row } from "mantine-react-table";
import { useMemo } from "react";

const Create = Object.assign({}, FormMain, {
  Button: Button,
  TextInput: TextInput,
  Select: Select,
  Table: MantineSelectableTable,
  Wrapper: FormWrapper,
  ButtonBox: FormButtonBox,
  InventoriesAutoComplete: InventoriesAutoComplete,
  LocationAutoComplete: LocationAutoComplete,
  NumberInput: NumberInput,
  BarcodeInput: MultiBarcodeInput,
});

type PageKeyType = "equipment" | "plan"; // pageKey의 가능한 값들을 정의합니다.

type ItemType<T extends PageKeyType> = T extends "equipment"
  ? AuthSignupPost201ResponseEquipmentWorksInner
  : ProductionPlansGet200ResponseRowsInnerWorksInner; // pageKey에 따른 item의 타입을 정의합니다.

interface WorkInputFormProps<T extends PageKeyType> {
  workRowData: ItemType<T>;
}

export function RawMaterialInputAllForm<T extends PageKeyType>(
  params: WorkInputFormProps<T>,
): JSX.Element {
  const { closeModal } = useModal();
  const queryClient = useQueryClient();
  const actions = useWorkActions();

  const workData =
    "routingData" in params.workRowData
      ? (params.workRowData as ProductionPlansGet200ResponseRowsInnerWorksInner)
      : undefined;

  const fromLocationCode = workData?.locationSetting?.fromLocation?.code;
  const targetLocationCode = workData?.locationSetting?.toLocation?.code;
  const targetLocationName = workData?.locationSetting?.toLocation?.name;

  const bomItemCodes = workData?.routingData?.routingBoms?.map((value) => value.itemCode);

  const routingBoms = workData?.routingData?.routingBoms;

  const convertedRoutingBoms = routingBoms?.map((bom) => ({
    itemCode: bom.itemCode,
    itemName: bom.item?.name,
    routingCode: bom.routingCode,
  }));

  const form = useForm({
    initialValues: {
      itemCode: "",
      sourceLocationCode: fromLocationCode,
      useBarcode: false,
      barcodeInput: "",
      worksBulkInputPutRequest: [],
    },
  });

  const { data, refetch } = useInventoriesFormFetch({
    itemCodes: form.values.itemCode ?? undefined,
    locationCode: form.values.sourceLocationCode,
    lotId: form.values.barcodeInput,
  });

  const { mutate: workInputMutate } = useMutation(
    (params: ProductionActionApiWorksBulkInputPutRequest) =>
      mutateInventories.workInputAll(params).mutationFn(undefined),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["inventories"]);
        actions.setWorks((prevWorks) => []);
        closeModal(form.values);
        customAlert("원부자재 일괄투입에 성공하였습니다.", "원부자재 일괄투입 성공", "green");
      },
      onError: (error: any) => {
        customAlert(
          handleErrorResponse({ error, errorContext: "원부자재 일괄투입" }),
          "원부자재 일괄투입 실패",
          "red",
        );
      },
    },
  );

  const columnsBoms = useMemo<MRT_ColumnDef[]>(
    () => [
      {
        accessorKey: "itemCode",
        header: "품목코드",
      },
      {
        accessorKey: "itemName",
        header: "품목명",
      },
      {
        accessorKey: "routingCode",
        header: "라우팅코드",
      },
    ],
    [],
  );

  // 순서 로트명 품목명 Grade Color 형태 필요 원자재량 투입량 Actions
  const columns = useMemo<MRT_ColumnDef<any>[]>(
    () => [
      {
        accessorKey: "order",
        header: "순서",
      },
      {
        accessorKey: "itemCode",
        header: "품목코드",
      },
      {
        accessorKey: "itemName",
        header: "품목명",
      },
      {
        accessorKey: "lotName",
        header: "로트명",
      },
      {
        accessorKey: "lotExpiration",
        header: "로트 만료일",
      },
      {
        accessorKey: "sourceLocationName",
        header: "로트 위치",
      },
      {
        accessorKey: "quantity",
        header: "수량",
        Cell: ({ cell }) => {
          return (
            <NumberInput
              w={"6rem"}
              {...form.getInputProps(`worksBulkInputPutRequest.${cell.row.index}.quantity`)}
              value={Number(cell.getValue())}
            />
          );
        },
      },
      {
        accessorKey: "actions",
        header: "Actions",
        Cell: ({ row }) => (
          <Button variant="outline" color="red" onClick={() => handleRowDelete(row)}>
            삭제
          </Button>
        ),
      },
    ],
    [],
  );

  const handleOnSubmit = () => {
    const request = form.values.worksBulkInputPutRequest.map(
      (data: WorksBulkInputPutRequestWorkLotPairsInner) => ({
        workId: workData?.id as number,
        lotId: data.lotId,
        sourceLocationCode: data.sourceLocationCode,
        targetLocationCode: data.targetLocationCode,
        quantity: String(data.quantity),
      }),
    );

    workInputMutate({
      worksBulkInputPutRequest: {
        workLotPairs: request,
      },
    });
  };

  const handleOnRowSelection = (selectedRows: any) => {
    form.setFieldValue("itemCode", selectedRows[0]?.itemCode);
  };

  const handleRowDelete = (row: MRT_Row<any>) => {
    form.removeListItem("worksBulkInputPutRequest", row.index);
  };

  const refetchHandler = async () => {
    const data = await refetch();
    const inventoryData = data?.data?.data;
    const noData = !inventoryData?.length;
    if (noData) {
      form.setFieldValue("barcodeInput", "");
      customAlert(
        "존재하지 않은 로트입니다.",
        "바코드에 해당하는 로트가 존재하지 않습니다.",
        "red",
      );
    } else {
      const { lotId, itemCode, itemName, lot, locationCode, locationName, quantity } =
        inventoryData[0] as ItemsGet200ResponseRowsInnerLotsInnerInventoriesInner;
      form.setFieldValue("barcodeInput", "");
      form.insertListItem("worksBulkInputPutRequest", {
        lotName: lot?.name,
        lotExpiration: lot?.expiration,
        lotId,
        itemCode,
        itemName,
        sourceLocationCode: locationCode,
        sourceLocationName: locationName,
        targetLocationCode: targetLocationCode,
        quantity: quantity,
      });
    }
  };

  const handleKeyDown = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Unidentified") {
      // 바코드 리더기 또는 입력기 문제로 인한 불명확한 키는 무시
      e.preventDefault();
      return;
    }

    if (e.key === "Enter") {
      if (!form.values.barcodeInput) return;
      if (checkDuplicateLot(form.values.barcodeInput)) return;
      await form.setFieldValue("barcodeInput", form.values.barcodeInput);
      await form.setFieldValue("useBarcode", true);
      await refetchHandler();
    }
  };

  const handleBlur = async (event: React.FocusEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;
    if (!inputValue) return;
    if (checkDuplicateLot(inputValue)) return;
    await form.setFieldValue("barcodeInput", inputValue);
    await form.setFieldValue("useBarcode", true);
    await refetchHandler();
  };

  const checkDuplicateLot = (value: string) => {
    if (form.values.worksBulkInputPutRequest.some((data: any) => data.lotId === Number(value))) {
      customAlert("중복된 로트입니다.", "중복된 로트는 입력할 수 없습니다.", "red");
      form.setFieldValue("barcodeInput", "");
      return true;
    }
    return false;
  };

  const handleInventories = async (value: string | null) => {
    if (value === null) return;
    if (!checkDuplicateLot(value)) {
      const { lotId } = data?.data?.filter(
        (data) => data.lotId === Number(value),
      )[0] as ItemsGet200ResponseRowsInnerLotsInnerInventoriesInner;
      await form.setFieldValue("barcodeInput", String(lotId));
      await form.setFieldValue("useBarcode", true);
      await refetchHandler();
    }
  };

  return (
    <Create.Wrapper w={"100%"}>
      <Create.Table
        enableRowSelection={true}
        enableMultiRowSelection={false}
        columns={columnsBoms}
        data={convertedRoutingBoms ?? []}
        onRowSelection={handleOnRowSelection}
      />
      <Flex justify={"space-between"} gap={"xl"}>
        <Create.LocationAutoComplete
          disabled
          w={"100%"}
          withAsterisk
          label={"로케이션"}
          {...form.getInputProps("sourceLocationCode")}
        />
      </Flex>
      <Flex justify={"space-between"} gap={"xl"}>
        <Create.InventoriesAutoComplete
          w={"100%"}
          disabled={!form.values.sourceLocationCode}
          label={"로트 정보"}
          value={form.values.barcodeInput}
          onChange={handleInventories}
          query={
            form.values.itemCode
              ? {
                  itemCode: form.values.itemCode,
                  locationCode: form.values.sourceLocationCode,
                  quantity: { $gt: 0 },
                }
              : {
                  itemCode: undefined,
                  locationCode: form.values.sourceLocationCode,
                  quantity: { $gt: 0 },
                }
          }
        />
      </Flex>
      <Flex justify={"space-between"}>
        <Create.BarcodeInput
          handleBlur={handleBlur}
          onKeyDown={handleKeyDown}
          value={form.values.barcodeInput}
          {...form.getInputProps("barcodeInput")}
          label={"바코드 정보 입력"}
          required
        />
        <Flex gap={"xl"} align={"center"}>
          <Create.TextInput
            withAsterisk
            disabled
            label={"입고 로케이션 코드"}
            value={targetLocationCode}
          />
        </Flex>
      </Flex>
      <Create.Table
        enableRowSelection={false}
        localization={{
          noRecordsToDisplay: "투입할 원부자재를 선택해주세요.",
        }}
        columns={columns}
        data={form.values.worksBulkInputPutRequest.map(
          (data: WorksBulkInputPutRequestWorkLotPairsInner, index: number) => ({
            order: index + 1,
            ...data,
          }),
        )}
      />
      <Create.ButtonBox>
        <Create.Button color="gray" onClick={closeModal}>
          취소
        </Create.Button>
        <Create.Button onClick={handleOnSubmit}>일괄 투입</Create.Button>
      </Create.ButtonBox>
    </Create.Wrapper>
  );
}
